import { FC, ReactNode } from 'react'

import styled, { css } from 'styled-components'

import avatar from 'src/Assets/Png/avatar.png'
import CheckMarkIcon from 'src/Assets/Svg/pages-icons/CheckMark.svg?react'
import CrossIcon from 'src/Assets/Svg/common/fullCross.svg?react'

import { SmartImage } from '../SmartImage'

export interface LabelArtist {
  title: ReactNode
  imgUrl?: string
}

export interface LabelForArtistProps {
  title: LabelArtist['title']
  onClick: () => void
  imgUrl?: LabelArtist['imgUrl']
  selected?: boolean
  disabled?: boolean
  hasCross?: boolean
}

const Container = styled.button<{
  $selected?: boolean
  $hasCross?: boolean
  disabled?: boolean
}>`
  display: flex;
  align-items: center;
  padding: 8px 12px 9px 12px;
  gap: 8px;
  width: fit-content;
  border-radius: 8px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  font-family: 'Segoe UI';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  border: 1px solid;
  transition: all 0.2s ease;

  ${({ theme, $selected, $hasCross, disabled }) => {
    if (disabled) {
      return css`
        background-color: ${theme.color.white};
        border-color: ${theme.color.white};
        color: ${theme.color.lightGrey};
        svg {
          path {
            transition: all 0.2s ease;
            fill: ${theme.color.lightGrey} !important;
          }
        }
        img {
          opacity: 0.5;
        }
      `
    }
    if ($selected || $hasCross) {
      return css`
        background-color: ${theme.color.white};
        border-color: ${theme.color.darkGrey};
        color: ${theme.color.darkGrey};
        svg {
          path {
            transition: all 0.2s ease;
            fill: ${theme.color.darkGrey} !important;
          }
        }

        &:hover {
          background-color: ${theme.color.hoverWhite};
          border-color: ${theme.color.darkGrey};
          color: ${theme.color.darkGrey};
        }

        &:focus-visible {
          background-color: ${theme.color.hoverWhite};
          border-color: ${theme.color.darkGreen};
          color: ${theme.color.darkGrey};
          outline: none;
        }

        &:active {
          background-color: ${theme.color.white};
          border-color: ${theme.color.slateGrey};
          color: ${theme.color.lightGrey};
          svg {
            path {
              transition: all 0.2s ease;
              fill: ${theme.color.lightGrey} !important;
            }
          }
        }
      `
    }
    return css`
      background-color: ${theme.color.white};
      border-color: ${theme.color.lightGrey};
      color: ${theme.color.darkGrey};

      &:hover {
        background-color: ${theme.color.hoverWhite};
        border: 1px solid ${theme.color.lightGrey};
        color: ${theme.color.darkGrey};
      }

      &:focus-visible {
        background-color: ${theme.color.hoverWhite};
        border-color: ${theme.color.darkGreen};
        color: ${theme.color.darkGrey};
        outline: none;
      }

      &:active {
        background-color: ${theme.color.white};
        border-color: ${theme.color.slateGrey};
        color: ${theme.color.slateGrey};

        svg {
          path {
            transition: all 0.2s ease;
            fill: ${theme.color.lightGrey} !important;
          }
        }
      }
    `
  }}
`
const StyledImage = styled(SmartImage)`
  border-radius: 50%;
  width: 32px;
  height: 32px;
`

const Text = styled.span`
  display: flex;
  text-align: center;
  gap: 4px;
`

export const LabelForArtist: FC<LabelForArtistProps> = ({
  title,
  onClick,
  imgUrl,
  selected,
  disabled,
  hasCross,
}) => (
  <Container
    $selected={selected}
    $hasCross={hasCross}
    disabled={disabled}
    onClick={onClick}
    translate='no'
  >
    <StyledImage src={imgUrl} alt='user avatar' fallbackSrc={avatar} />
    <Text>{title}</Text>
    {hasCross && <CrossIcon role='cross-icon' />}
    {!hasCross && selected && <CheckMarkIcon role='check-icon' />}
  </Container>
)
